import Icon, { Icons } from '@/components/elements/Icon';
import { UnderlineTabs } from '@/components/modules/Tabs';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Tab } from '../types';
import { animated, useSpringRef, useTransition } from '@react-spring/web';

export interface SwapSearchProps {
  tab: Tab;
  tabs: [all: ReactNode, nfts: ReactNode, tokens: ReactNode];
  onTabChange: (v: Tab) => void;
  search: string;
  onSearch: (v: string) => void;
}

export const SwapSearch = ({
  tab,
  tabs,
  onTabChange,
  onSearch,
  search,
}: SwapSearchProps) => {
  const availableTabs = useMemo(() => {
    return ['all', 'nfts', 'tokens'] as const;
  }, []);
  const t = useTranslations('Swap');
  const [index, setIndex] = useState<number>(() => availableTabs.indexOf(tab));
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  // handle which way to transition and which tab to go to
  useEffect(() => {
    const current = availableTabs.indexOf(tab);
    if (current < index) {
      setDirection('next');
    }

    if (current > index) {
      setDirection('prev');
    }

    if (index !== current) {
      setIndex(current);
    }
  }, [tab, availableTabs, index]);

  const transRef = useSpringRef();

  const transitions = useTransition(index, {
    ref: transRef,
    keys: null,
    onStart: () => {},
    from: {
      opacity: 0,
      transform: `translate3d(${direction === 'prev' ? '10%' : '-5%'},0,0)`,
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)', top: 0 },
    leave: {
      opacity: 0,
      transform: `translate3d(${direction === 'prev' ? '-5%' : '10%'},0,0)`,
    },
  });

  // trigger each time index changes
  useEffect(() => {
    transRef.start();
  }, [index, transRef]);

  return (
    <div className="bg-gray-100 flex flex-col">
      <div className="group bg-gray-200 p-4">
        <div className="relative">
          <span className="absolute w-5 h-full flex items-center justify-center left-3">
            <Icon
              name={Icons.SEARCH}
              className="text-gray-400 transform transition-transform group-focus-within:scale-105"
              aria-hidden="true"
              size={16}
            />
          </span>
          <input
            type="search"
            name="nftx_v3_search"
            id="nftx_v3_search"
            className="rounded-full h-10 md:h-12 bg-gray-100 block w-full border border-gray-300 py-1.5 pl-10 text-gray-500 placeholder:text-gray-400 placeholder:focus:text-gray-400 focus:outline-none focus:ring-0 focus:border-gray-400/75 text-base"
            placeholder={t('search')}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>
      <nav className="bg-gray-100">
        <UnderlineTabs
          tabs={availableTabs.map((id) => ({
            id,
            label: t(`Tabs.${id}`),
          }))}
          activeTab={tab}
          direction="horizontal"
          size="small"
          onTabChange={(t) => onTabChange(t as SwapSearchProps['tab'])}
        />
      </nav>

      <div className="h-[60vh] max-h-[36rem] overflow-y-auto hide-scroll relative">
        {transitions((style, i) => {
          return (
            <animated.div
              style={style}
              key={i}
              className="w-full h-full absolute will-change-auto top-0"
            >
              {tabs[i]}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

export default SwapSearch;
